/**
 * Query construction, with the reference's subtle rules made structural rather than
 * remembered. Each one below cost somebody an hour to discover; none of them should ever be
 * rediscovered by a screen author.
 */

import type { Sentiment } from './types.js';

// ---------------------------------------------------------------- dates

/**
 * The three date modes are MUTUALLY EXCLUSIVE - sending two is a 400. A discriminated union
 * makes that a compile error rather than a runtime surprise, which is the entire reason this
 * is a type and not three optional fields.
 *
 *   day       ?date=YYYY-MM-DD                one calendar day, in the AGENCY's timezone
 *   dayRange  ?fromDate=&toDate=              both ends INCLUSIVE
 *   instants  ?from=&to=                      from inclusive, to EXCLUSIVE
 *
 * A bare date is not UTC. That distinction is the whole point of the design: an agency left on
 * the UTC default resolves a Melbourne "today" ten hours out, silently.
 */
export type DateFilter =
  | { readonly mode: 'day'; readonly date: string }
  | { readonly mode: 'dayRange'; readonly fromDate: string; readonly toDate: string }
  | { readonly mode: 'instants'; readonly from: string; readonly to: string };

/**
 * A recurring daily window, evaluated per row in the agency's timezone - so it stays correct
 * across a daylight-saving change. It COMBINES with a DateFilter rather than replacing it:
 * "business hours this month" needs both.
 *
 * An end earlier than the start wraps midnight, deliberately. Surface that as a named
 * "Overnight" preset rather than asking a person to reason about 22:00-02:00.
 */
export interface TimeWindow {
  readonly fromTime: string; // HH:mm
  readonly toTime: string;   // HH:mm
}

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;

export function validateDateFilter(f: DateFilter): void {
  switch (f.mode) {
    case 'day':
      if (!DATE_RE.test(f.date)) throw new RangeError(`date must be YYYY-MM-DD, got "${f.date}"`);
      return;
    case 'dayRange':
      if (!DATE_RE.test(f.fromDate) || !DATE_RE.test(f.toDate)) {
        throw new RangeError('fromDate and toDate must both be YYYY-MM-DD');
      }
      // The API returns 400 for a backwards range. Catching it here turns a round trip into
      // a control that cannot express the invalid state in the first place.
      if (f.fromDate > f.toDate) throw new RangeError('fromDate is after toDate');
      return;
    case 'instants':
      if (Number.isNaN(Date.parse(f.from)) || Number.isNaN(Date.parse(f.to))) {
        throw new RangeError('from and to must be ISO-8601 instants');
      }
      if (Date.parse(f.from) >= Date.parse(f.to)) throw new RangeError('from is not before to');
      return;
  }
}

export function validateTimeWindow(w: TimeWindow): void {
  if (!TIME_RE.test(w.fromTime) || !TIME_RE.test(w.toTime)) {
    throw new RangeError('fromTime and toTime must be HH:mm');
  }
  // Equal start and end is the ambiguous case: zero-length, or the whole day? Refuse it.
  if (w.fromTime === w.toTime) throw new RangeError('fromTime and toTime are the same');
}

// ---------------------------------------------------------------- sorting and paging

export const CONVERSATION_SORT = ['StartedAt', 'Duration', 'Sentiment', 'AssignedAt'] as const;
export const CLIENT_SORT = ['Name', 'CreatedAt'] as const;
export const CONNECTION_SORT = ['ConnectionName', 'LastSyncedAt', 'SyncStatus'] as const;
export const USER_SORT = ['CreatedAt', 'Name', 'Email', 'Role'] as const;

export type SortDirection = 'Asc' | 'Desc';
export interface Sort<F extends string> {
  readonly sortBy: F;
  readonly sortDirection: SortDirection;
}

/** The hard ceiling. Above this the API CLAMPS silently rather than rejecting - see `Pager`. */
export const MAX_LIMIT = 100;
export const DEFAULT_LIMIT = 25;

/**
 * Keyset paging that cannot silently lose rows.
 *
 * Two traps, both live:
 *
 *  1. **`?limit=500` returns 200 with 100 rows.** Clamped, not rejected. A caller that asks
 *     for 500 and assumes it got them skips 400 rows with no error anywhere. So asking for
 *     more than 100 throws HERE, locally, where the mistake is visible - and `collectAll()`
 *     exists for the case that motivated it.
 *  2. **A cursor encodes the sort it was issued for.** Replaying it under a different sort is
 *     a 400 telling you to start again. So the cursor is held together with its sort and
 *     DISCARDED automatically when the sort changes - a screen author changing the sort should
 *     not have to remember to reset the page, and forgetting looks like a bug rather than an
 *     error.
 */
export class Pager<F extends string> {
  #sort: Sort<F>;
  #cursor: string | null = null;
  #exhausted = false;
  readonly limit: number;

  constructor(sort: Sort<F>, limit: number = DEFAULT_LIMIT) {
    if (!Number.isInteger(limit) || limit < 1) throw new RangeError('limit must be a positive integer');
    if (limit > MAX_LIMIT) {
      throw new RangeError(
        `limit ${limit} exceeds the API maximum of ${MAX_LIMIT}. The API clamps silently ` +
        `rather than rejecting, so asking for more would skip rows without telling you. ` +
        `Use collectAll() to read every page.`,
      );
    }
    this.#sort = sort;
    this.limit = limit;
  }

  get sort(): Sort<F> { return this.#sort; }
  get cursor(): string | null { return this.#cursor; }
  /** True once a page has come back with `nextCursor: null`. */
  get exhausted(): boolean { return this.#exhausted; }

  /** Change the sort. Always resets to page one, because the old cursor is now invalid. */
  setSort(sort: Sort<F>): void {
    if (sort.sortBy === this.#sort.sortBy && sort.sortDirection === this.#sort.sortDirection) return;
    this.#sort = sort;
    this.reset();
  }

  /** Any filter change restarts the query too. */
  reset(): void { this.#cursor = null; this.#exhausted = false; }

  accept(nextCursor: string | null): void {
    this.#cursor = nextCursor;
    this.#exhausted = nextCursor === null;
  }
}

// ---------------------------------------------------------------- assembling

export interface ConversationFilters {
  readonly clientId?: string;
  readonly agentId?: string;
  readonly sentiment?: Sentiment;
  /**
   * Free text, matched as a CASE-INSENSITIVE SUBSTRING by the API — so "booking" finds
   * "Booking confirmed". Until 2026-09-13 the server did an exact match while every client said
   * "contains", which meant a partial word returned an empty page that read as "there are none".
   */
  readonly outcome?: string;
  /**
   * The source a call arrived through — `phone`, `web`. Matched exactly, case-insensitively.
   *
   * Get the values that actually exist from `listChannels()` rather than hardcoding a list:
   * the provider writes this field and may add to it, at which point a fixed list silently stops
   * matching. Same lesson the `outcome` field already taught.
   */
  readonly channel?: string;
  readonly assignedUserId?: string;
  readonly unassignedOnly?: boolean;
  readonly date?: DateFilter;
  readonly timeWindow?: TimeWindow;
  /**
   * Words to find across the transcript, summary, title and outcome.
   *
   * **A filter, not a separate search.** It sits here with everything else so that searching and
   * then narrowing by date or agent is one list rather than two worlds — which is the whole
   * requirement: *"once I've done the filter, I should be able to play them back as normal."*
   *
   * Syntax is what people already expect from a search box, interpreted by Postgres:
   * bare words are AND-ed, `"a quoted phrase"` must appear adjacent, `-word` excludes. Matching
   * is stemmed English, so `cancelling` finds `cancelled`. Malformed input never errors.
   *
   * Rows that matched carry a {@link Conversation.matchSnippet}.
   */
  readonly text?: string;
}

export function buildQuery(
  filters: ConversationFilters,
  sort?: Sort<string>,
  pager?: Pager<string>,
): URLSearchParams {
  const q = new URLSearchParams();
  const put = (k: string, v: string | number | boolean | undefined) => {
    if (v !== undefined && v !== '') q.set(k, String(v));
  };

  put('clientId', filters.clientId);
  put('agentId', filters.agentId);
  put('sentiment', filters.sentiment);
  put('outcome', filters.outcome);
  put('channel', filters.channel);
  put('assignedUserId', filters.assignedUserId);
  // `q` on the wire, `text` in this interface. The short name is what a URL wants and the long
  // one is what a call site wants; conflating them would make one of the two read badly.
  put('q', filters.text?.trim());
  if (filters.unassignedOnly) q.set('unassignedOnly', 'true');

  if (filters.date) {
    validateDateFilter(filters.date);
    const d = filters.date;
    if (d.mode === 'day') q.set('date', d.date);
    else if (d.mode === 'dayRange') { q.set('fromDate', d.fromDate); q.set('toDate', d.toDate); }
    else { q.set('from', d.from); q.set('to', d.to); }
  }

  if (filters.timeWindow) {
    validateTimeWindow(filters.timeWindow);
    q.set('fromTime', filters.timeWindow.fromTime);
    q.set('toTime', filters.timeWindow.toTime);
  }

  if (sort) { q.set('sortBy', sort.sortBy); q.set('sortDirection', sort.sortDirection); }
  if (pager) {
    q.set('limit', String(pager.limit));
    if (pager.cursor) q.set('cursor', pager.cursor);
  }
  return q;
}
